/**
 * Employee Personal Data Tab Component
 * 
 * This component is extracted from EmployeeForm.jsx for modularity.
 * It maintains the exact same structure and data-testid attributes.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const PersonalTab = ({ formData, handleChange, isEdit }) => {
  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle className="font-heading">Persönliche Daten</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Personalnummer - prominent placement */}
        <div className="p-4 bg-secondary/30 rounded-lg border border-border/40">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="employee_number" className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">Personal-Nr.</Badge>
                Personalnummer
              </Label>
              <Input
                id="employee_number"
                value={formData.employee_number || ''}
                onChange={(e) => handleChange('employee_number', e.target.value)}
                placeholder={isEdit ? '' : 'Wird automatisch generiert'}
                data-testid="employee-number"
              />
              <p className="text-xs text-muted-foreground">
                {isEdit ? 'Kann manuell angepasst werden' : 'Leer lassen für automatische Vergabe (z.B. EMP-00001)'}
              </p>
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                  id="is_active"
                />
                <Label htmlFor="is_active" className="font-normal cursor-pointer">
                  Mitarbeiter aktiv
                </Label>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="last_name">Nachname *</Label>
            <Input
              id="last_name"
              value={formData.last_name || ''}
              onChange={(e) => handleChange('last_name', e.target.value)}
              data-testid="employee-lastname"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="first_name">Vorname *</Label>
            <Input
              id="first_name"
              value={formData.first_name || ''}
              onChange={(e) => handleChange('first_name', e.target.value)}
              data-testid="employee-firstname"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Private E-Mail</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Optional"
              data-testid="employee-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Telefon</Label>
            <Input
              id="phone"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              data-testid="employee-phone"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mobile">Mobil</Label>
            <Input
              id="mobile"
              value={formData.mobile || ''}
              onChange={(e) => handleChange('mobile', e.target.value)}
              data-testid="employee-mobile"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="date_of_birth">Geburtsdatum</Label>
            <DatePickerDE
              value={formData.date_of_birth}
              onChange={(v) => handleChange('date_of_birth', v)}
              placeholder="Geburtsdatum wählen"
              testId="employee-dob"
              defaultMonth={`${new Date().getFullYear() - 30}-01-01`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="place_of_birth">Geburtsort</Label>
            <Input
              id="place_of_birth"
              value={formData.place_of_birth || ''}
              onChange={(e) => handleChange('place_of_birth', e.target.value)}
              placeholder="z.B. Berlin"
              data-testid="employee-pob"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gender">Geschlecht</Label>
            <Select value={formData.gender || ''} onValueChange={(v) => handleChange('gender', v)}>
              <SelectTrigger data-testid="employee-gender">
                <SelectValue placeholder="Auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="männlich">Männlich</SelectItem>
                <SelectItem value="weiblich">Weiblich</SelectItem>
                <SelectItem value="divers">Divers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="nationality">Nationalität</Label>
            <Input
              id="nationality"
              value={formData.nationality || ''}
              onChange={(e) => handleChange('nationality', e.target.value)}
              data-testid="employee-nationality"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalTab;
